import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import { Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/D';
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const getDefaultFilters = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
  return {
    date_start: `${year}-${month}-01`,
    date_end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
  };
};

function TransferList({ onEdit }) {
  const [transfers, setTransfers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const width = useWindowWidth();

  const fetchTransfers = (url = '/transfers', currentFilters = filters) => {
    const urlObj = new URL(url, window.location.origin);
    const page = urlObj.searchParams.get('page');
    const params = new URLSearchParams(currentFilters);
    if (page) {
      params.set('page', page);
    }
    const pathname = urlObj.pathname.replace(/^\/api/, '');
    const fullUrl = `${pathname}?${params.toString()}`;

    api.get(fullUrl)
      .then(response => {
        setTransfers(response.data.data);
        setPagination(response.data);
      })
      .catch(error => {
        console.error('Houve um erro ao buscar as transferências!', error);
      });
  };

  useEffect(() => {
    fetchTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilters = () => {
    fetchTransfers('/transfers');
    if (width < 768) {
      setShowFilterModal(false);
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = { date_start: '', date_end: '' };
    setFilters(clearedFilters);
    fetchTransfers('/transfers', clearedFilters);
    if (width < 768) {
      setShowFilterModal(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Tem certeza?',
      text: 'Deseja excluir esta transferência? O saldo dos bancos será revertido.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        api.delete(`/transfers/${id}`)
          .then(() => {
            Swal.fire({ title: 'Excluída!', text: 'Transferência excluída com sucesso.', icon: 'success', confirmButtonText: 'Ok' });
            fetchTransfers(pagination?.current_page_url || '/transfers');
          })
          .catch(error => {
            console.error('Houve um erro ao excluir a transferência!', error);
            Swal.fire({ title: 'Erro!', text: 'Erro ao excluir transferência.', icon: 'error', confirmButtonText: 'Ok' });
          });
      }
    });
  };

  const decodePaginationLabel = (label) => {
    return label
      .replace(/&laquo;/g, '«')
      .replace(/&raquo;/g, '»')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  const renderPaginationLinks = () => {
    if (!pagination || !pagination.links) {
      return null;
    }

    return (
      <nav>
        <ul className="pagination justify-content-center">
          {pagination.links.map((link, index) => (
            <li key={index} className={`page-item ${link.active ? 'active' : ''} ${!link.url ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => link.url && fetchTransfers(link.url)}
              >
                {decodePaginationLabel(link.label)}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    );
  };

  const renderFilters = () => (
    <div className="card card-body mb-3">
      <div className="row">
        <div className="col-md-4">
          <label>Data De</label>
          <input type="date" name="date_start" value={filters.date_start} onChange={handleFilterChange} className="form-control" />
        </div>
        <div className="col-md-4">
          <label>Data Até</label>
          <input type="date" name="date_end" value={filters.date_end} onChange={handleFilterChange} className="form-control" />
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-md-12">
          <button className="btn btn-primary me-2" onClick={handleApplyFilters}>Aplicar Filtros</button>
          <button className="btn btn-secondary" onClick={handleClearFilters}>Limpar Filtros</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <h2>Transferências</h2>

      {/* Mobile: botão filtros + modal */}
      <div className="d-md-none">
        <button className="btn btn-primary mb-3" onClick={() => setShowFilterModal(true)}>Filtros</button>
        <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Filtros</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {renderFilters()}
          </Modal.Body>
        </Modal>

        {transfers.map(transfer => (
          <div className="card mb-2" key={transfer.id}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <strong className="text-primary">{formatCurrency(transfer.amount)}</strong>
              </div>
              <div className="small mb-1">
                <span className="text-muted">Origem:</span> {transfer.from_bank?.name}
              </div>
              <div className="small mb-1">
                <span className="text-muted">Destino:</span> {transfer.to_bank?.name}
              </div>
              <div className="small mb-1">
                <span className="text-muted">Data:</span> {formatDate(transfer.transfer_date)}
              </div>
              {transfer.description && (
                <div className="small mb-2">
                  <span className="text-muted">Obs:</span> {transfer.description}
                </div>
              )}
              <div className="d-flex gap-2">
                <button className="btn btn-danger btn-sm flex-fill" onClick={() => handleDelete(transfer.id)}>Excluir</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: filtros + tabela */}
      <div className="d-none d-md-block">
        <button className="btn btn-primary mb-3" onClick={() => setShowFilters(!showFilters)}>
          {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
        </button>
        {showFilters && renderFilters()}
        <table className="table table-striped table-hover">
          <thead className="thead-dark">
            <tr>
              <th>Origem</th>
              <th>Destino</th>
              <th>Valor</th>
              <th>Data</th>
              <th>Descrição</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {transfers.map(transfer => (
              <tr key={transfer.id}>
                <td>{transfer.from_bank?.name}</td>
                <td>{transfer.to_bank?.name}</td>
                <td>{formatCurrency(transfer.amount)}</td>
                <td>{formatDate(transfer.transfer_date)}</td>
                <td>{transfer.description}</td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(transfer.id)}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {renderPaginationLinks()}
    </div>
  );
}

export default TransferList;
